import type { ColumnsType } from "antd/es/table"
import type React from "react"
import { useCallback, useEffect, useRef, useState } from "react"

import { applyWidthsToConfig } from "../components/EntityTable/entity-table.utils.ts"
import type { ResizableHeaderCellProps } from "../components/Table/ResizableHeaderCell.tsx"
import type { ColumnConfigItem } from "../containers/User/redux/ui-preferences.types.ts"
import { useDebouncedCallback } from "./use-debounced-callback.ts"

interface UseColumnResizeParams {
  fullConfig: ColumnConfigItem[]
  setColumns: (next: ColumnConfigItem[]) => void
  enabled?: boolean
  reorderable?: boolean
  minWidth?: number
  maxWidth?: number
}

// Drag-to-resize column widths for a plain AntD Table (reordering handled
// separately). Widths persist (debounced) into the same column config as
// order/visibility. Pair with `ResizableHeaderCell` as `components.header.cell`.
export function useColumnResize<T>({
  fullConfig,
  setColumns,
  enabled = true,
  reorderable = false,
  minWidth,
  maxWidth
}: UseColumnResizeParams) {
  const [widths, setWidths] = useState<Record<string, number>>({})
  const widthsRef = useRef(widths)
  useEffect(() => {
    widthsRef.current = widths
  }, [widths])

  const resizedKeysRef = useRef<Set<string>>(new Set())

  const fullConfigRef = useRef(fullConfig)
  useEffect(() => {
    fullConfigRef.current = fullConfig
  }, [fullConfig])

  const setColumnsRef = useRef(setColumns)
  useEffect(() => {
    setColumnsRef.current = setColumns
  }, [setColumns])

  const flushWidths = useCallback(() => {
    if (resizedKeysRef.current.size === 0) return
    const pending: Record<string, number> = {}
    resizedKeysRef.current.forEach((key) => {
      const width = widthsRef.current[key]
      if (width != null) pending[key] = width
    })
    resizedKeysRef.current.clear()
    setColumnsRef.current(applyWidthsToConfig(fullConfigRef.current, pending))
  }, [])

  const persistWidths = useDebouncedCallback(flushWidths, 500)

  useEffect(() => () => flushWidths(), [flushWidths])

  const handleResize = useCallback(
    (key: string, width: number) => {
      resizedKeysRef.current.add(key)
      setWidths((prev) => ({ ...prev, [key]: width }))
      persistWidths()
    },
    [persistWidths]
  )

  const tableWrapRef = useRef<HTMLDivElement>(null)

  // Reported so callers can keep the table from ever being narrower than its
  // viewport (below that it detaches from the fixed-right columns).
  const [containerWidth, setContainerWidth] = useState(0)
  useEffect(() => {
    const root = tableWrapRef.current
    if (!root || typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width
      if (width) setContainerWidth(Math.floor(width))
    })
    observer.observe(root)
    return () => observer.disconnect()
  }, [])

  // Pin every column to its rendered width before a resize starts, else a
  // fixed-layout table redistributes widths and the dragged column jumps.
  const pinAllWidths = useCallback(() => {
    const root = tableWrapRef.current
    if (!root) return
    const snapshot: Record<string, number> = {}
    root.querySelectorAll<HTMLElement>("th[data-col-key]").forEach((cell) => {
      const colKey = cell.getAttribute("data-col-key")
      if (colKey) {
        snapshot[colKey] = Math.round(cell.getBoundingClientRect().width)
      }
    })
    setWidths((prev) => ({ ...snapshot, ...prev }))
  }, [])

  const decorateColumns = useCallback(
    (cols: ColumnsType<T>): ColumnsType<T> =>
      cols.map((col) => {
        const key = col.key == null ? undefined : String(col.key)
        const liveWidth = key ? widths[key] : undefined
        // Fixed / keyless columns aren't resizable or reorderable (keeps AntD sticky intact).
        if (!enabled || key == null || col.fixed) {
          return liveWidth != null ? { ...col, width: liveWidth } : col
        }
        const cellProps: Partial<ResizableHeaderCellProps> = {
          columnKey: key,
          reorderable,
          onColumnResize: handleResize,
          onResizeStart: pinAllWidths,
          minWidth,
          maxWidth
        }
        return {
          ...col,
          width: liveWidth ?? col.width,
          onHeaderCell: () =>
            cellProps as unknown as React.HTMLAttributes<HTMLTableCellElement>
        }
      }),
    [
      widths,
      enabled,
      reorderable,
      handleResize,
      pinAllWidths,
      minWidth,
      maxWidth
    ]
  )

  const resetWidths = useCallback(() => {
    resizedKeysRef.current.clear()
    setWidths({})
  }, [])

  return { widths, containerWidth, tableWrapRef, decorateColumns, resetWidths }
}
